import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  User,
  Users as UsersIcon,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  CreditCard,
  LayoutDashboard,
  Home,
  Stethoscope,
  UserCog,
  FileText,
  ClipboardList,
  Scale,
  Bell,
  type LucideIcon,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useNotifications } from "../../contexts/NotificationContext";
import { ROLES } from "../../constants/roles";

interface SidebarProps {
  userRole: string;
  collapsed: boolean;
  onToggle: () => void;
}

interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
}

const getNavigationItems = (role: string): NavItem[] => {
  if (role === ROLES.SUPERADMIN) {
    return [
      { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
      { name: "Doctors", href: "/doctors", icon: Stethoscope },
      { name: "Public Users", href: "/public-users", icon: UsersIcon },
      { name: "Users", href: "/users", icon: UserCog },
      { name: "MDT Reports", href: "/reports", icon: FileText },
      { name: "Audit Logs", href: "/activity", icon: ClipboardList },
      { name: "Notifications", href: "/notifications", icon: Bell },
    ];
  }

  if (role === ROLES.PUBLIC) {
    return [
      { name: "Home", href: "/dashboard", icon: Home },
      { name: "MDT Reports", href: "/reports", icon: FileText },
      { name: "Notifications", href: "/notifications", icon: Bell },
      { name: "Legal Documents", href: "/legal", icon: Scale },
    ];
  }

  return [
    { name: "Home", href: "/dashboard", icon: LayoutDashboard },
    { name: "Patients", href: "/patients", icon: UsersIcon },
    { name: "MDT Reports", href: "/reports", icon: FileText },
    { name: "Audit Logs", href: "/activity", icon: ClipboardList },
    { name: "Notifications", href: "/notifications", icon: Bell },
    { name: "Legal Documents", href: "/legal", icon: Scale },
  ];
};

const Sidebar: React.FC<SidebarProps> = ({ userRole, collapsed, onToggle }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { unreadCount } = useNotifications();

  const navigationItems = getNavigationItems(userRole);

  const isSubDashboardActive =
    location.pathname === "/subscriptions/dashboard" ||
    location.pathname === "/subscriptions";

  const isListingActive =
    location.pathname === "/subscriptions/listing" ||
    location.pathname.startsWith("/subscriptions/listing") ||
    location.pathname.startsWith("/subscription-listing") ||
    location.pathname.startsWith("/admin/subscriptions");

  const isSubscriptionsActive =
    isSubDashboardActive ||
    isListingActive ||
    location.pathname.startsWith("/subscriptions");

  const [subscriptionDropdownOpen, setSubscriptionDropdownOpen] = useState(
    () => isSubscriptionsActive
  );

  useEffect(() => {
    if (isSubscriptionsActive) {
      setSubscriptionDropdownOpen(true);
    }
  }, [isSubscriptionsActive]);

  const isActive = (href: string) =>
    href === "/dashboard"
      ? location.pathname.startsWith(href)
      : location.pathname === href || location.pathname.startsWith(`${href}/`);

  const renderContent = (isCollapsed: boolean) => (
    <div className="flex flex-col h-full w-full overflow-hidden">
      <div
        className={`w-full border-b border-neutral-200 shrink-0 py-4 ${
          isCollapsed
            ? "flex items-center justify-center px-0"
            : "flex items-center justify-center relative px-4"
        }`}
      >
        {!isCollapsed && (
          <img
            src="/logo/DiagnomIQ.png"
            alt="AI Patient Care Platform"
            className="h-9 w-auto max-w-[170px] object-contain object-left"
          />
        )}
        <button
          onClick={onToggle}
          className={`hidden md:inline-flex h-7 w-7 items-center justify-center rounded-full border border-neutral-200 bg-white text-neutral-500 shadow-sm hover:text-primary-600 hover:border-primary-300 transition-colors ${
            !isCollapsed ? "absolute right-4 top-1/2 -translate-y-1/2" : ""
          }`}
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </button>
        <button
          onClick={() => setOpen(false)}
          className="md:hidden absolute right-4 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-700"
          aria-label="Close menu"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <nav
        className={`flex-1 overflow-y-auto w-full py-4 space-y-1 ${
          isCollapsed ? "px-1" : "px-3"
        }`}
      >
        {navigationItems.map((item, index) => (
          <Link
            key={`${item.href}-${index}`}
            to={item.href}
            onClick={() => setOpen(false)}
            title={isCollapsed ? item.name : undefined}
            className={`group relative flex w-full min-w-0 items-center rounded-lg text-sm font-medium transition-all duration-200 ${
              isCollapsed ? "justify-center px-0 py-3" : "gap-3 px-4 py-3"
            } ${
              isActive(item.href)
                ? "bg-primary-50 text-primary-600 font-semibold"
                : "text-neutral-600 hover:bg-neutral-50 hover:text-primary-600"
            }`}
          >
            {isActive(item.href) && (
              <span
                className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-1 rounded-r bg-primary-500"
                aria-hidden="true"
              />
            )}
            <div className="relative">
              <item.icon className="h-[18px] w-[18px] flex-shrink-0" />
              {item.name === "Notifications" && unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-danger-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-danger-500 border-2 border-white"></span>
                </span>
              )}
            </div>
            {!isCollapsed && (
              <div className="flex flex-1 items-center justify-between truncate">
                <span className="truncate">{item.name}</span>
                {item.name === "Notifications" && unreadCount > 0 && (
                  <span className="bg-danger-100 text-danger-600 py-0.5 px-2 rounded-full text-xs font-bold">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </div>
            )}
          </Link>
        ))}

        {/* Subscription Dropdown Menu for SuperAdmin */}
        {userRole === ROLES.SUPERADMIN && (
          <div className="space-y-1">
            <button
              type="button"
              onClick={() => {
                if (isCollapsed) {
                  onToggle();
                  setSubscriptionDropdownOpen(true);
                } else {
                  setSubscriptionDropdownOpen((prev) => !prev);
                }
              }}
              title={isCollapsed ? "Subscription" : undefined}
              className={`group relative flex w-full min-w-0 items-center rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${
                isCollapsed ? "justify-center px-0 py-3" : "justify-between px-4 py-3"
              } ${
                isSubscriptionsActive
                  ? "bg-primary-50 text-primary-600 font-semibold"
                  : "text-neutral-600 hover:bg-neutral-50 hover:text-primary-600"
              }`}
            >
              {isSubscriptionsActive && (
                <span
                  className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-1 rounded-r bg-primary-500"
                  aria-hidden="true"
                />
              )}
              <div
                className={`flex items-center min-w-0 ${
                  isCollapsed ? "justify-center" : "gap-3"
                }`}
              >
                <CreditCard className="h-[18px] w-[18px] flex-shrink-0" />
                {!isCollapsed && <span className="truncate">Subscription</span>}
              </div>
              {!isCollapsed && (
                <ChevronDown
                  className={`h-4 w-4 transition-transform duration-200 flex-shrink-0 text-neutral-400 group-hover:text-primary-600 ${
                    subscriptionDropdownOpen ? "rotate-180 text-primary-600" : ""
                  }`}
                />
              )}
            </button>

            {/* Dropdown Submenu */}
            {subscriptionDropdownOpen && !isCollapsed && (
              <div className="space-y-1 pl-9 pr-1 pt-0.5 pb-1">
                <Link
                  to="/subscriptions/dashboard"
                  onClick={() => setOpen(false)}
                  className={`flex items-center w-full px-3 py-2 text-xs font-medium rounded-lg transition-colors cursor-pointer text-left ${
                    isSubDashboardActive
                      ? "bg-primary-50 text-primary-600 font-semibold"
                      : "text-neutral-600 hover:bg-neutral-50 hover:text-primary-600"
                  }`}
                >
                  <span className="truncate">Dashboard</span>
                </Link>

                <Link
                  to="/subscriptions/listing"
                  onClick={() => setOpen(false)}
                  className={`flex items-center w-full px-3 py-2 text-xs font-medium rounded-lg transition-colors cursor-pointer text-left ${
                    isListingActive
                      ? "bg-primary-50 text-primary-600 font-semibold"
                      : "text-neutral-600 hover:bg-neutral-50 hover:text-primary-600"
                  }`}
                >
                  <span className="truncate">Subscription Listing</span>
                </Link>
              </div>
            )}
          </div>
        )}
      </nav>

      <div className="border-t border-neutral-200 p-4">

        <button
          onClick={() => navigate("/profile")}
          className={`w-full text-left transition-colors duration-200 ${
            isCollapsed ? "flex justify-center" : "flex items-center space-x-3"
          } ${isActive("/profile") ? "bg-primary-50 rounded-md" : "hover:bg-neutral-100 hover:rounded-md"} ${
            isCollapsed ? "py-1" : "px-2 py-2"
          }`}
        >
          <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
            <User className="h-4 w-4 text-primary-600" />
          </div>
          {!isCollapsed && (
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{user?.name ?? "User"}</div>
              <div className="text-neutral-500 capitalize text-xs truncate">
                {(userRole ?? "").replace("_", " ").toLowerCase()}
              </div>
            </div>
          )}
        </button>
        <button
          onClick={logout}
          className={`mt-3 flex items-center w-full rounded-md text-sm text-neutral-700 hover:bg-neutral-100 transition-colors duration-200 ${
            isCollapsed ? "justify-center px-0 py-2" : "px-3 py-2"
          }`}
        >
          <LogOut className={`h-4 w-4 ${isCollapsed ? "" : "mr-2"}`} />
          {!isCollapsed && "Sign out"}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white shadow-sm border-b border-neutral-200">
        <div className="flex items-center justify-between px-4 h-16">
          <button
            onClick={() => setOpen(true)}
            className="text-neutral-700 hover:text-primary-600"
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </button>

          <img
            src="/logo/DiagnomIQ.png"
            alt="AI Patient Care Platform"
            className="h-10 w-25 object-contain"
          />
        </div>
      </div>

      {/* Desktop sidebar */}
      <aside
        className={`hidden md:flex fixed top-0 left-0 bottom-0 ${
          collapsed ? "w-[68px]" : "w-60"
        } bg-white shadow-sm border-r border-neutral-200 z-40 transition-[width] duration-200`}
      >
        {renderContent(collapsed)}
      </aside>

      {/* Mobile sidebar overlay */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-40 z-50 transition-opacity duration-200 md:hidden ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setOpen(false)}
      />
      <aside
        className={`fixed top-0 left-0 bottom-0 w-60 bg-white shadow-lg z-50 transition-transform duration-200 md:hidden ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {renderContent(false)}
      </aside>
    </>
  );
};

export default Sidebar;